<template>
	<div>
		<b-card v-if="orgData && orgData.fullName" class="mb-2">
			<b-row align-v="center" style="margin-bottom: 20px;">
				<b-col lg="10" md="6">
					<div v-if="orgData.logoUrl" class="logo mt-2 mb-4">
						<b-img :src="cPicture"></b-img>
					</div>
				</b-col>
				<b-col lg="2" md="6">
					<select class="custom-select lang-select" v-model="selectedLanguage" @change="relocate()">
						<option v-for="language in getFormLanguages" :key="language.id" :value="language.id">
							{{ language.text }}
						</option>
					</select>
				</b-col>
			</b-row>
			<blockquote class="blockquote">
				<p class="mb-o">
					{{ !isNullOrEmpty(cHeaderText) ? cHeaderText : $t(this.entityTexts[$route.params.entity].text) }}
				</p>
				<footer class="blockquote-footer">
					<cite :title="cOrgName">{{ cOrgName }}</cite>
				</footer>
			</blockquote>
		</b-card>
		<b-row v-if="loadingFormData" style="min-height: 60vh;" align-v="center" align-h="center">
			<div>
				<b-row align-h="center">
					<b-spinner style="width: 4rem; height: 4rem;"></b-spinner>
				</b-row>
				<b-row align-h="center"> {{ $t("loading") }}... </b-row>
			</div>
		</b-row>
		<b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>
		<b-form @submit.prevent="" v-else>
			<NetBoxSearch v-if="isCompanyForm" @business-set="setNetboxData($event)"></NetBoxSearch>
			<b-card v-for="(header, index) in formHeaders" :key="header" class="mb-2" :title="$t(header)">
				<div v-if="index+1 == formHeaders.length && !isNullOrEmpty(cFooterText)" class="mb-2">{{ cFooterText }}</div>
				<template v-for="field in formSchema[header]">
					<b-form-group v-if="field.fieldType == 'fieldgroup'" :key="field.name" :label="field.label" class="mb-3">
						<b-card v-for="(v, index) in $v.values[field.name].$each.$iter" :key="index" class="mb-3">
							<FormField
								v-for="subfield in field.fields"
								:key="subfield.name"
								:field="subfield"
								v-model="v[subfield.name].$model"
								:state="validateSubState(field.name, subfield.name, index)"
								:errors="getErrorMessagesFor(v[subfield.name], subfield)"
								@uploadFile="uploadFile"
								@removeFile="removeFile"
								:files="[]"
							></FormField>
							<b-button
								v-if="field.repeated"
								variant="outline-danger"
								class="float-right"
								size="sm"
								@click.prevent="removeFieldGroup(field, index)"
								>{{ $t("delete") }} {{ field.buttonLabel || field.name }}</b-button
							>
						</b-card>

						<b-button size="sm" v-if="field.repeated" variant="primary" @click.prevent="addFieldGroup(field)">
							{{ $t("addNew") }} {{ field.buttonLabel || field.name }}</b-button
						>
						<template v-if="validateState(field.name) === false">
							<div
								class="invalid-feedback"
								style="display:block;"
								v-for="msg in getErrorMessagesFor($v.values[field.name], field)"
								:key="msg"
							>
								{{ msg }}
							</div>
						</template>
						<b-form-text v-if="field.required"> {{ $t("requiredField") }} </b-form-text>
						<template v-if="field.helpTexts && field.helpTexts.length && field.helpTexts.length >= 1">
							<b-form-text v-for="text in field.helpTexts" :key="text">{{ $t(text) }}</b-form-text>
						</template>
						<hr v-if="field.repeated" />
					</b-form-group>

					<FormField
						v-else
						:field="field"
						:key="'f_' + field.name"
						:state="validateState(field.name)"
						:errors="getErrorMessagesFor($v.values[field.name], field)"
						:defaultValue="field.value"
						:orgName="cOrgName"
						v-model="$v.values[field.name].$model"
						@uploadFile="uploadFile"
						@removeFile="removeFile"
						:files="values[field.name + '_fileId'] || []"
					></FormField>
				</template>
			</b-card>
			<b-row>
				<b-col>
					<b-btn block variant="primary" @click.prevent="submit">{{ $t("send") }}</b-btn>
				</b-col>
				<b-col>
					<b-btn block variant="outline-danger" @click.prevent="reset">{{ $t("empty") }}</b-btn>
				</b-col>
			</b-row>
		</b-form>
		<Success ref="success" hideTime="3000" successText="netbox-success" />
	</div>
</template>

<script>
// @ is an alias to /src
import FormField from "@/components/FormField.vue";
import NetBoxSearch from "@/components/NetBoxSearch.vue";
import Success from "@/components/SuccessModal.vue";
import axios from "axios";
import _ from "lodash";

import { minLength, maxLength, required, minValue, maxValue } from "vuelidate/lib/validators";
import { isValid, parseISO } from "date-fns";

export default {
	name: "DynamicForm",
	props: ["formConfig", "orgData"],
	components: {
		FormField,
		NetBoxSearch,
		Success
	},
	data() {
		return {
			show: false,
			loadingFormData: true,
			error: null,
			values: {},
			rawForm: [],
			sectionOrdering: null,
			selectedLanguage: "fi",
			headerFooterData: {
				langFi: true,
				langSv: true,
				langEn: true,
				orgName: "",
				headerFi: "",
				headerSv: "",
				headerEn: "",
				picture: "",
				footerFi: "",
				footerSv: "",
				footerEn: ""
			},
			entityTexts: {
				student: {
					text: "studentEntityText"
				},
				workplace: {
					text:
						"Tällä lomakkeella syötetyt tiedot tallentuvat kunnan tai kunnallisen toimijan asiakastietojärjestelmään. Tietoa hyödynnetään työllisyyttä edistävissä palveluissa sekä asiakastietojen hallinnassa."
				},
				jobApplicant: {
					text:
						"Tällä lomakkeella syötetyt tiedot tallentuvat kunnan tai kunnallisen toimijan asiakastietojärjestelmään. Tietoa hyödynnetään työllisyyttä edistävissä palveluissa sekä asiakastietojen hallinnassa."
				},
				startingEntrepreneur: {
					text: "startingEntrepreneurTextEntityText"
				},
				company: {
					text: "companyEntityText"
				},
				integration: {
					text: "integrationEntityText"
				}
			},
			formLanguages: {
				startingEntrepreneur: [
					{
						text: "Suomi",
						id: "fi"
					},
					{
						text: "English",
						id: "en"
					},
					{
						text: "Svenska",
						id: "se"
					}
				],
				company: [
					{
						text: "Suomi",
						id: "fi"
					},
					{
						text: "English",
						id: "en"
					},
					{
						text: "Svenska",
						id: "se"
					}
				],
				student: [
					{
						text: "Suomi",
						id: "fi"
					}
				],
				workplace: [
					{
						text: "Suomi",
						id: "fi"
					}
				],
				jobApplicant: [
					{
						text: "Suomi",
						id: "fi"
					}
				],
				integration: [
					{
						text: "Suomi",
						id: "fi"
					},
					{
						text: "English",
						id: "en"
					}
				]
			}
		};
	},
	validations() {
		if (!this.rawForm) {
			return {};
		}

		let values = {};

		let validations = {
			minLength: minLength,
			maxLength: maxLength,
			minCount: minLength,
			maxCount: maxLength,
			required: required,
			minValue: minValue,
			maxValue: maxValue
		};

		const fileRequired = fieldName => () => {
			return Boolean(this.values[fieldName + "_fileId"].length > 0);
		};

		const date = value => {
			if (value) {
				let parsed = parseISO(value, "yyyy-MM-dd");
				return isValid(parsed);
			}
			return true;
		};

		const regex = field => value => {
			let valid = true;

			field.regexValidations.forEach(rv => {
				let exp = new RegExp(rv.regex);
				if (!exp.test(value)) {
					valid = false;
				}

				if (!valid) {
					return;
				}
			});

			return valid;
		};

		this.rawForm.forEach(f => {
			let fieldConfigKeys = _.keys(f);
			if (!values[f.name]) {
				values[f.name] = {};
			}

			let target = values[f.name];

			if (f.fieldType == "fieldgroup") {
				if (!target.$each) {
					target.$each = {};

					f.fields.forEach(sf => {
						target.$each[sf.name] = {};

						let subFieldConfigKeys = _.keys(sf);
						subFieldConfigKeys.forEach(k => {
							if (k == "required" && sf[k] === false) {
								return;
							}

							if (validations[k] && sf[k] !== null) {
								if (typeof validations[k] == "function" && sf[k] !== true) {
									target.$each[sf.name][k] = validations[k](sf[k]);
								} else {
									target.$each[sf.name][k] = validations[k];
								}
							}
						});
					});
				}
			}

			fieldConfigKeys.forEach(k => {
				if (f.fieldType == "file" && k == "required" && f[k]) {
					target[k] = fileRequired(f.name);
					return;
				}

				if (f.fieldType == "date") {
					target[k] = date;
				}

				if (k == "regexValidations") {
					target[k] = regex(f);
				}

				if (k == "required" && f[k] === false) {
					return;
				}

				if (validations[k] && f[k] !== null) {
					if (typeof validations[k] == "function" && f[k] !== true) {
						target[k] = validations[k](f[k]);
					} else {
						target[k] = validations[k];
					}
				}
			});
		});
		return { values };
	},
	methods: {
		showSuccess() {
			this.$bvModal.show("success-modal");
		},
		setNetboxData(data) {
			let netBoxName = "";
			let abbreviation = "";
			const netBoxBusinessId = data.vat_identifier;
			let street = "";
			let zipCode = "";
			let city = "";

			if (data.names.length > 0) {
				let primary = data.names.filter(r => r.type == "primary");
				netBoxName = primary.length > 0 ? primary[0].name : "";

				let auxiliary = data.names.filter(r => r.type == "auxiliary");
				abbreviation = auxiliary.length > 0 ? auxiliary[0].name : "";
			}

			if (data.addresses.length > 0) {
				let address = data.addresses[0];
				if (address) {
					street = address.street;
					zipCode = address.zipcode;
					city = address.city;
				}
			}

			const netBoxFields = [
				{
					formFieldName: "yrityksen_virallinen_nimi",
					netBoxValue: netBoxName
				},
				{
					formFieldName: "y-tunnus",
					netBoxValue: netBoxBusinessId
				},
				{
					formFieldName: "aputoiminimi,_lyhenne_tms.",
					netBoxValue: abbreviation
				},
				{
					formFieldName: "postiosoite",
					netBoxValue: street
				},
				{
					formFieldName: "postinumero",
					netBoxValue: zipCode
				},
				{
					formFieldName: "postitoimipaikka",
					netBoxValue: city
				}
			];

			netBoxFields.forEach(r => {
				// let x = this.rawForm.filter(f => f.name == r.formFieldName)[0];
				// if (x) {
				// 	x.value = r.netBoxValue;
				// }
				this.values[r.formFieldName] = r.netBoxValue;
			});
			this.showSuccess();
			this.$refs.success.autoHide();
		},
		relocate() {
			let org = this.$route.params.org;
			let entity = this.$route.params.entity;
			let lang = this.selectedLanguage;
			this.$router.push({
				name: "Form",
				params: { org, entity, lang }
			});
			location.reload();
			// this.$router.push(`/${org}/${entity}/${language}`);
		},
		uploadFile({ field, fileId }) {
			this.values[field.name + "_fileId"].push(fileId);
		},
		removeFile({ field }) {
			this.values[field.name] = null;
			this.values[field.name + "_fileId"] = [];
		},
		async submit() {
			console.log(JSON.stringify(this.formPostValues));
			this.$v.values.$touch();

			if (this.$v.values.$invalid) {
				let str = this.$t("Tarkista lomakkeen kentät");
				alert(str);
				return;
			}

			let response;

			try {
				response = await axios.post(this.formConfig.endpoint, this.formPostValues);
			} catch (error) {
				if (error && error.response && error.response.data) {
					const { data } = error.response;
					if (data.validationErrors) {
						let keys = _.keys(data.validationErrors);

						let error =
							"Tarkista kentät: " +
							keys
								.map(k => {
									let field = this.rawForm.filter(f => f.name == k).shift();

									if (field) {
										return field.label;
									}
									return null;
								})
								.filter(label => label != null)
								.join(", ");

						alert(error);
					} else {
						this.error = data;
					}
				}
			}

			if (response && response.status == 200) {
				/* submit success */
				let lang = this.selectedLanguage;
				this.$router.push({
					name: "ThankYou",
					params: { lang }
				});
				// this.$router.push("/ThankYou");
			}
		},
		reset() {
			let values = {};

			let arrayTypes = ["checkbox", "treeselect"];

			this.rawForm.forEach(f => {
				if (f.fieldType == "fieldgroup") {
					let emptyInstance = {};

					f.fields.forEach(subField => {
						emptyInstance[subField.name] = null;

						if (arrayTypes.indexOf(subField.fieldType) >= 0) {
							emptyInstance[subField.name] = [];
						}

						if (f.fieldType == "file") {
							emptyInstance[subField.name + "_fileId"] = [];
						}
					});

					if (f.repeated) {
						values[f.name] = [];
					} else {
						values[f.name] = [emptyInstance];
					}
				} else {
					values[f.name] = null;

					if (arrayTypes.indexOf(f.fieldType) >= 0) {
						values[f.name] = [];
					}

					if (f.fieldType == "file") {
						values[f.name + "_fileId"] = [];
					}

					if (f.fieldType == "select") {
						let defaultOption = f.options.filter(o => o.selected);
						values[f.name] = defaultOption.length > 0 ? defaultOption[0].id : "";
					}
				}
			});

			this.values = values;
			this.$v.values.$reset();
		},
		buttonRadioStyle(state) {
			let styles = {
				true: "outline-success",
				false: "outline-danger",
				null: "outline-secondary"
			};

			return styles[state];
		},
		removeFieldGroup(field, index) {
			this.$delete(this.values[field.name], index);
		},
		addFieldGroup(field) {
			let newObj = {};

			let subFields = field.fields;

			subFields.forEach(sf => {
				newObj[sf.name] = null;
			});

			this.values[field.name].push(newObj);
		},
		getFields(name) {
			if (this.$v.values[name].$each) {
				return this.$v.values[name].$each.$iter;
			} else {
				return [this.$v.values[name]];
			}
		},
		validateState(name) {
			if (!this.$v) {
				return null;
			}

			if (!this.$v.values) {
				return null;
			}

			if (!this.$v.values[name]) {
				return null;
			}

			const { $dirty, $error } = this.$v.values[name];
			return $dirty ? !$error : null;
		},
		validateSubState(field, subfield, index) {
			const { $dirty, $error /* , $invalid */ } = this.$v.values[field].$each.$iter[index][
				subfield
			];

			return $dirty ? !$error : null;
		},
		getErrorMessagesFor(vfield, field) {
			if (!vfield || !vfield.$params) {
				return [];
			}
			let messages = [];

			let rules = vfield.$params;
			let ruleKeys = _.keys(rules);

			let invalidRuleKeys = ruleKeys.filter(k => !vfield[k]);
			// ${$t()}
			let messageMap = {
				minLength: rule =>
					`${this.$t(field.label)} ${this.$t("mustBeAtLeast")} ${rule.min} ${this.$t(
						"charsLong"
					)}.`,
				maxLength: rule =>
					`${this.$t(field.label)} ${this.$t("cantBeMoreThan")} ${rule.max} ${this.$t(
						"charsLong"
					)}.`,
				minCount: rule => `${this.$t("min")} ${rule.min} ${this.$t("choices")}.`,
				maxCount: rule => `${this.$t("max")} ${rule.max} ${this.$t("choices")}.`,
				minValue: rule => `${this.$t("mustBeAtLeast")} ${rule.min}.`,
				maxValue: rule => `${this.$t("cantBeMoreThan")} ${rule.max}.`,
				required: () => 
					field.labelHyperlink 
					? field.labelHyperlink.text + ` ${this.$t("isRequired")}.`
					: `${this.$t(field.label)}` + ` ${this.$t("isRequired")}.`,
				fileRequired: () => `${this.$t(field.label)} ${this.$t("isRequired")}.`
			};

			invalidRuleKeys.forEach(rule => {
				if (typeof messageMap[rule] == "function") {
					messages.push(messageMap[rule](vfield.$params[rule]));
				}
			});

			return messages;
		},
		
		isNullOrEmpty(variable) {
			if (variable === null || typeof variable === 'undefined' || variable.length == 0) {
				return true;
			}
			return false;
		},

		getHeaderFooterData(docCode) {
			axios.get(`HeaderFooter/${docCode}`).then(resp => {
				if (resp.data) {
					this.headerFooterData.langFi = resp.data.langFi;
					this.headerFooterData.langSv = resp.data.langSv;
					this.headerFooterData.langEn = resp.data.langEn;
					this.headerFooterData.orgName = resp.data.orgName;
					this.headerFooterData.headerFi = resp.data.headerFi;
					this.headerFooterData.headerSv = resp.data.headerSv;
					this.headerFooterData.headerEn = resp.data.headerEn;
					this.headerFooterData.picture = resp.data.picture;
					this.headerFooterData.footerFi = resp.data.footerFi;
					this.headerFooterData.footerSv = resp.data.footerSv;
					this.headerFooterData.footerEn = resp.data.footerEn;
				}
				// check if Finnish is disabled
				if (!this.headerFooterData.langFi && !this.$route.params.lang) {
					this.selectedLanguage = this.headerFooterData.langSv ? "se" : this.headerFooterData.langEn ? "en" : "fi";
					this.relocate();
				}
			});
		},

		setDataFromUrl() {
			const urlFields = [
				{
					formFieldName: "yrityksen_virallinen_nimi",
					paramName: "ac-name"
				},
				{
					formFieldName: "y-tunnus",
					paramName: "ac-businessid"
				},
				{
					formFieldName: "aputoiminimi,_lyhenne_tms.",
					paramName: "ac-abbr"
				},
				{
					formFieldName: "postiosoite",
					paramName: "ac-street"
				},
				{
					formFieldName: "postinumero",
					paramName: "ac-zipcode"
				},
				{
					formFieldName: "postitoimipaikka",
					paramName: "ac-city"
				}
			];
			
			const queryString = window.location.search;
			const urlParams = new URLSearchParams(queryString);
			urlFields.forEach(r => {
				if(urlParams.has(r.paramName)){					
					var paramValue = urlParams.get(r.paramName);
					console.log(r.paramName + ": " + paramValue);
					this.values[r.formFieldName] = paramValue;
				}
			});
		},

	},
	computed: {
		isCompanyForm() {
			if (this.$route.params.entity == "company") {
				return true;
			}
			return false;
		},
		formPostValues() {
			let fields = _.cloneDeep(this.rawForm);

			fields.forEach((f, fi) => {
				if (f.fieldType == "file") {
					let newField = {};
					newField.name = f.name;
					newField.fieldType = "file";
					newField.valueArray = this.values[newField.name + "_fileId"];
					newField.value = null;

					fields[fi] = newField;
					return;
				}

				if (f.fieldType != "fieldgroup") {
					f = { name: f.name, fieldType: f.fieldType };
					if (Array.isArray(this.values[f.name])) {
						f.valueArray = this.values[f.name];
					} else {
						f.value = this.values[f.name];
					}
					fields[fi] = f;
				} else {
					let valueGroups = this.values[f.name];

					f = { name: f.name, fields: f.fields, fieldType: f.fieldType, groupValues: [] };

					valueGroups.forEach((vg, i) => {
						let group = [];
						f.fields.forEach((sf /* , si */) => {
							let subfield = { name: sf.name, fieldType: sf.fieldType };

							if (Array.isArray(valueGroups[i][sf.name])) {
								subfield.valueArray = vg[sf.name];
							} else {
								subfield.value = vg[sf.name];
							}

							group.push(subfield);
						});
						f.groupValues.push(group);
						fields[fi] = f;
					});
				}
			});

			return fields;
		},
		formHeaders() {
			return _.orderBy(_.keys(this.formSchema), [r => this.sectionOrdering[r]]);
		},
		formSchema() {
			let groupedByHeader = _.groupBy(this.rawForm, r => r.section);

			let headers = _.keys(groupedByHeader);

			headers.forEach(h => {
				let arr = groupedByHeader[h];

				groupedByHeader[h] = _.orderBy(arr, g => g.order);
			});

			return groupedByHeader;
		},
		getFormLanguages() {
			let entity = this.$route.params.entity;

			let languages = this.formLanguages[entity];
			// Filter out unselected languages
			languages = languages.filter(l => 
				(l.id.toLowerCase() === "fi" && this.headerFooterData.langFi) ||
				(l.id.toLowerCase() === "se" && this.headerFooterData.langSv) ||
				(l.id.toLowerCase() === "en" && this.headerFooterData.langEn)
			);
			if (languages.length > 0) {
				return languages;
			} else {
				return this.formLanguages[entity];
			}
		},
		cFormId() {
			return this.$route.params.entity;
		},
		cOrgName() {
			return !this.isNullOrEmpty(this.headerFooterData.orgName) ? this.headerFooterData.orgName : this.orgData.fullName;
		},
		cHeaderText() {
			if (!this.selectedLanguage) {
				return this.headerFooterData.headerFi;
			}
			if (this.selectedLanguage.toLowerCase() === "sv" || this.selectedLanguage.toLowerCase() === "se") {
				return this.headerFooterData.headerSv;
			} else if (this.selectedLanguage.toLowerCase() === "en") {
				return this.headerFooterData.headerEn;
			} else {
				return this.headerFooterData.headerFi;
			}
		},
		cFooterText() {
			if (!this.selectedLanguage) {
				return this.headerFooterData.footerFi;
			}
			if (this.selectedLanguage.toLowerCase() === "sv" || this.selectedLanguage.toLowerCase() === "se") {
				return this.headerFooterData.footerSv;
			} else if (this.selectedLanguage.toLowerCase() === "en") {
				return this.headerFooterData.footerEn;
			} else {
				return this.headerFooterData.footerFi;
			}
		},
		cPicture() {
			if (this.headerFooterData && this.headerFooterData.picture) {
				return this.headerFooterData.picture;
			} else {
				return this.orgData.logoUrl;
			}
		}
	},
	async beforeMount() {
		this.getHeaderFooterData(this.cFormId);
		if (this.$route.params.lang) {
			this.$i18n.locale = this.$route.params.lang;
			this.selectedLanguage = this.$route.params.lang;
		}

		if (!this.formConfig) {
			console.error("No form config");
			return;
		}

		if (!this.formConfig.endpoint) {
			console.error("No endpoint");
			return;
		}

		let response = null;

		this.loadingFormData = true;

		try {
			response = await axios.get(this.formConfig.endpoint);
		} catch (error) {
			console.error(error);
			if (error && error.response && error.response.data) {
				console.error(error.response.data);
				this.error = error.response.data;
			}
		}

		const { data } = response;

		if (data) {
			this.rawForm = data.formFields;
			this.sectionOrdering = data.sections;
			this.reset();
		}

		this.loadingFormData = false;
		if(this.isCompanyForm){
			this.setDataFromUrl();
		}
	}
};
</script>

<style lang="scss">
.view-lomake {
	margin-top: 25px;
	margin-bottom: 25px;
}
// .top-container{
// 	display: flex;
// 	flex-direction: row;
// 	flex-wrap: nowrap;
// 	align-items: baseline;
// 	.lang-select{
// 		width:
// 	}
// }

div.logo {
	max-width: 70%;

	img {
		height: 4rem;
		width: auto;
		object-fit: contain;
	}
}
</style>
